import { DescriptionCode } from "@/data/description-codes.data";
import React, { useEffect, useState } from "react";
import { ErrorType } from "@prisma/client";
import { useStatusWebsocket } from "@/components/pages/convert/hooks/use-status-websocket";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";

type WebSocketMessageHandler = (message: any) => void;

export type ServiceProgressStatus =
  | ""
  | "in progress"
  | "waiting"
  | "preparing"
  | "complete"
  | "force-cancelled"
  | "cancelled";

export interface AnonymousProgressContextState {
  percent: number;
  status: ServiceProgressStatus;
  processId?: string;
  description: DescriptionCode;
  setPercent: (value: number) => void;
  setStatus: (status: ServiceProgressStatus) => void;
  setProcessId: (id: string) => void;
  resetContext: () => void;
  forceCancelCurrentProcess: () => Promise<void>;
}

export interface AnonymousBaseProgressContextProps {
  forceCancelProcess?: (id: string) => Promise<void>;
  handleCompleteMessage: (
    message: any,
    setDescription?: () => React.Dispatch<React.SetStateAction<string>>,
  ) => Promise<void>;
  handleErrorMessage?: (errorType: ErrorType) => void;
}

interface AnonymousProgressContextProps
  extends AnonymousBaseProgressContextProps {
  buildWebSocketUrl: (processId: string) => string;
}

export const shouldDisplayProgress = (status: ServiceProgressStatus) =>
  status === "in progress" || status === "preparing" || status === "waiting";

export const shouldDisplayStartProcessButton = (
  status: ServiceProgressStatus,
) => status !== "in progress" && status !== "waiting" && status !== "preparing";

export const useAnonymousProgressContext = ({
  forceCancelProcess,
  buildWebSocketUrl,
  handleCompleteMessage,
  handleErrorMessage,
}: AnonymousProgressContextProps) => {
  const [percent, setPercent] = useState(0);
  const [status, setStatus] = useState<ServiceProgressStatus>("");
  const [processId, setProcessId] = useState<string>();
  const [description, setDescription] = useState<DescriptionCode>("");

  const { createWebsocket, closeWebsocket } = useStatusWebsocket();
  const { showModal } = useGlobalModalContext();

  const resetContext = () => {
    setPercent(0);
    setStatus("");
    setDescription("");
    setProcessId("");
  };

  const handleCancel = (cancelStatus: ServiceProgressStatus) => {
    setStatus(cancelStatus);
    closeWebsocket();
    resetContext();
  };

  const forceCancelCurrentProcess = async () => {
    if (processId && forceCancelProcess) {
      await forceCancelProcess(processId);
    }
    handleCancel("force-cancelled");
  };

  const handleWebSocketMessage: WebSocketMessageHandler = async (message) => {
    if (message.status === "waiting") {
      setStatus(message.status);
      setDescription(message.message);
    } else if (message.status === "complete") {
      await handleCompleteMessage(message);
      setStatus(message.status);
      setDescription("");
      closeWebsocket();
      return;
    } else if (message.status === "error") {
      setStatus(message.status);
      setDescription("");
      closeWebsocket();
      handleErrorMessage
        ? handleErrorMessage(message.error)
        : showModal({
            type: "error_modal",
            props: {
              title: "Something went wrong",
              message: `Failed to process.`,
            },
          });
      return;
    } else if (message.status === "cancelled") {
      handleCancel(message.status);
      return;
    } else {
      setDescription(message.message);
      setPercent(message.percent);
      setStatus(message.status);
    }
  };

  const initWebsocket = () => {
    const wsUri = buildWebSocketUrl(processId!);
    createWebsocket(wsUri, handleWebSocketMessage);
  };

  useEffect(() => {
    if (processId) {
      console.log("initializaing websocket due to process id change");
      initWebsocket();
    }
  }, [processId]);

  return {
    percent,
    status,
    processId,
    description,
    setPercent,
    setStatus,
    setProcessId,
    initWebsocket,
    resetContext,
    forceCancelCurrentProcess,
  };
};
